<template>
  <v-card outlined class="expo">
    <div v-if="isBanned" class="locked_overlay flex-center">
      <div class="text-center">
        <v-icon size="60">mdi-lock-outline</v-icon>
        <p class="mb-0 font-weight-bold">
          {{ $t('SPACE.COPYWRITING.SPACE_IS_LOCKED') }}
        </p>
        <p class="mb-0">
          {{ $t('SPACE.COPYWRITING.PLEASE_GO_YOUR_PLAN') }}
          <router-link to="/update-account">
            {{ $t('COMMON.WORD.UPGRADE') }}
          </router-link>
          {{ $t('COMMON.WORD.UNLOCK_LIMIT') }}
        </p>
        <p class="my-2">{{ $t('COMMON.WORD.OR') }}</p>
        <v-btn small depressed color="primary" @click="doRecover">
          {{ $t('SPACE.WORD.UNLOCK') }}
        </v-btn>
      </div>
    </div>
    <v-hover #default="{ hover }" :disabled="isBanned || currentTab === 1">
      <v-img
        :aspect-ratio="16 / 9"
        :src="
          (expoData.contentMap.default.image &&
            expoData.contentMap.default.image.url) ||
          null
        "
        :class="{ btn: currentTab === 0 }"
        class="pos-r"
        @click="
          if (currentTab === 0)
            $router.push({
              name: 'exhibitions-space',
              params: { expoId: expoData.id, status: 'space' }
            });
        "
      >
        <template #placeholder>
          <v-img :src="defaultCover" />
        </template>
        <v-overlay
          :value="hover"
          absolute
          color="line"
          class="fill-all flex-center flex-column"
          v-if="currentTab === 1"
        ></v-overlay>
      </v-img>
    </v-hover>
    <v-card-text>
      <div class="d-flex align-center">
        <span class="w60p text-truncate neutral100--text">
          {{ expoData.contentMap.default.name }}
        </span>
        <v-spacer></v-spacer>
      </div>
    </v-card-text>
    <v-card-text
      class="d-flex"
      :class="{
        'justify-space-between': currentTab === 0,
        'justify-end': currentTab === 1
      }"
    >
      <template v-if="currentTab === 0">
        <v-sheet>
          <v-switch
            @change="$emit('onToggle', expoData)"
            v-model="exhibitionSwitch"
            inset
            class="exhibition_witch"
            :disabled="!exhibitionSwitch && toggleLimit"
            :label="`${
              expoData.status === 'published'
                ? $t('COMMON.WORD.PUBLISH')
                : $t('COMMON.WORD.UNPUBLISH')
            }`"
          ></v-switch>
        </v-sheet>
      </template>
      <v-menu :disabled="isBanned" :z-index="9">
        <template #activator="{ on }">
          <div class="flex-center">
            <v-icon v-on="on">mdi-dots-vertical</v-icon>
          </div>
        </template>
        <v-card color="neutral600" min-width="120">
          <SizeBox height="12" />
          <template v-if="currentTab === 0">
            <div
              class="menu_item"
              @click="
                $router.push({
                  name: 'exhibitions-space',
                  params: { expoId: expoData.id, status: 'space' }
                })
              "
            >
              <v-icon size="16">mdi-information-outline</v-icon>
              {{ $t('COMMON.WORD.INFO') }}
            </div>
            <div class="menu_item edit" @click="editSpace">
              <v-icon size="16">mdi-border-color</v-icon>
              {{ $t('COMMON.WORD.EDIT') }}
            </div>
            <div class="menu_item" @click="isShowSharFrame = true">
              <v-icon size="16">mdi-share</v-icon>
              {{ $t('COMMON.WORD.SHARE') }}
            </div>
            <div class="menu_item" @click="$emit('onRemove', expoData)">
              <v-icon size="16">mdi-trash-can-outline</v-icon>
              {{ $t('COMMON.WORD.DELETE') }}
            </div>
          </template>
          <template v-if="currentTab === 1">
            <div class="menu_item" @click="$emit('onDelete', expoData)">
              <v-icon size="16">mdi-trash-can-outline</v-icon>
              {{ $t('SPACE.COPYWRITING.REMOVE') }}
            </div>
            <div class="menu_item" @click="$emit('onRevive', expoData)">
              <v-icon size="16">mdi-arrow-u-left-top</v-icon>
              {{ $t('SPACE.COPYWRITING.RESTORE_SPACE') }}
            </div>
          </template>

          <SizeBox height="12" />
        </v-card>
      </v-menu>
    </v-card-text>

    <ShareFrame v-model="isShowSharFrame" :expo-data="expoData" />
  </v-card>
</template>

<script>
import ShareFrame from './ShareFrame.vue';
import defaultImage from '@/assets/images/default-room-image.png';
import defaultAdvancedImage from '@/assets/images/default-advanced-room-image.png';
import { recoverExhibition } from '@/api/panel/exhibitions';

export default {
  components: { ShareFrame },
  emits: ['onRevive', 'onDelete', 'onRemove', 'onToggle'],
  props: {
    expoData: {
      type: Object,
      default: () => ({}),
      required: true
    },
    currentTab: {
      type: Number,
      default: 0
    },
    reachLimit: {
      type: Boolean,
      default: false
    },
    toggleLimit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isShowSharFrame: false,
      exhibitionSwitch: false,
      init: true
    };
  },
  beforeMount() {
    this.setExhibitionStatus();
  },
  computed: {
    isBanned() {
      return this.expoData.status === 'banned';
    },
    defaultCover() {
      return this.expoData.artworkType != 'advanced'
        ? defaultImage
        : defaultAdvancedImage;
    }
  },

  methods: {
    setExhibitionStatus() {
      this.exhibitionSwitch =
        this.expoData.status === 'published' ? true : false;
      this.init = false;
    },
    async doRecover() {
      const lang = localStorage.getItem('language')
        ? localStorage.getItem('language').toLowerCase()
        : 'tw';
      const payload = {
        id: this.expoData.id,
        lang: lang
      };
      const Res = await recoverExhibition(payload);

      if (!Res.error) {
        this.$toast(this.$t('SPACE.COPYWRITING.UNLOCK_SUCCESSFUL'));
        this.$emit('refresh');
      } else {
        this.$toast.error(
          `${this.$t('SPACE.COPYWRITING.UNLOCK_FAILED')} ${Res?.error?.message}`
        );
      }
    },
    editSpace() {
      if (this.expoData.artworkType == 'advanced') {
        const { id, alias } = this.expoData;
        const editedLink = `${this.$vsnovaPageURL}/editor?sceneId=${id}&aliasId=${alias}`;
        window.open(editedLink);
      } else {
        this.$router.push({
          name: 'exhibitions-space',
          params: { expoId: this.expoData.id, status: 'edit' }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.locked_overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 9;

  background-color: #2121219c;
  .text-center {
    i {
      font-size: 60px;
    }
    p {
      font-size: 14px;
    }
  }
}

.empty_frame {
  border: 1px dashed #878787;
  border-radius: 6px;
}

.expo {
  border-color: $neutral600;

  ::v-deep .v-input--switch.v-input--is-dirty .v-input--switch__thumb {
    transform: translate(12px, 0) !important;
  }
  ::v-deep
    .v-input--switch--inset.v-input--is-dirty
    .v-input--selection-controls__ripple {
    transform: translate(15px, 0) !important;
  }
  ::v-deep .v-card__text {
    padding: 10px 16px;
    &:nth-of-type(3) {
      padding: 0 16px 12px;
    }
  }
}

.menu_item {
  cursor: pointer;
  padding: 6px 18px;
  font-size: 14px;
  display: flex;
  align-items: center;
  &:hover {
    background-color: $primary;
  }
  i {
    font-size: 16px;
    margin-right: 5px;
  }
  &.edit {
    i {
      margin-top: 5px;
    }
  }
}
::v-deep .v-input--selection-controls .v-input__slot {
  margin: 0;
}
::v-deep .v-input--selection-controls.v-input {
  margin: 0;
  padding: 0;
}
::v-deep .v-input__control .v-messages {
  display: none;
}
::v-deep .v-input--selection-controls .v-input__slot > .v-label {
  font-size: 12px;
}
::v-deep .v-input--switch__thumb {
  width: 12px;
  height: 12px;
  top: calc(50% - 5px);
}
::v-deep .v-input--switch--inset .v-input--selection-controls__input {
  width: 28px;
  height: 16px;
  margin-right: 0;
}
::v-deep .v-input--switch--inset .v-input--switch__track {
  top: calc(50% - 7px);
  width: 28px;
  height: 16px;
  left: -2px;
}
::v-deep .v-input--switch .v-input--selection-controls__ripple {
  width: 21px;
  height: 19px;
  left: -11px;
  top: calc(50% - 15px);
}
</style>